import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Loader2, RotateCw, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { pipelineApi, type PipelineRun } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/i18n/useApiError';

import { RelatedErrors } from './RelatedErrors';

interface DRCDrawerProps {
  run: PipelineRun | null;
  onClose: () => void;
}

/**
 * Side drawer that surfaces the raw DRC payload for one (source_id, run_id).
 *
 * Shows the structured high-value blocks first (assessment scores, the LLM's
 * Turtle output, validation summary) and falls back to the full JSON for
 * deep dives. The "Inspect related errors" link routes the analyst to the
 * error queue filtered by source_id when the run halted.
 */
export function DRCDrawer({ run, onClose }: DRCDrawerProps) {
  const { t } = useTranslation(['studio', 'common']);
  const apiError = useApiError();
  const queryClient = useQueryClient();
  const [retryResult, setRetryResult] = useState<{ runId: string } | null>(null);
  const open = !!run;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['supervision', 'drc', run?.source_id, run?.run_id],
    queryFn: () => pipelineApi.runDrc(run!.source_id, run!.run_id),
    enabled: open,
    retry: false,
    staleTime: 30_000,
  });

  const retryMut = useMutation({
    mutationFn: (sourceId: string) => pipelineApi.retry(sourceId),
    onSuccess: (resp) => {
      setRetryResult({ runId: resp.run_id });
      // The new run will land in the supervision list once the worker completes.
      void queryClient.invalidateQueries({ queryKey: ['supervision', 'runs'] });
    },
  });

  if (!run) return null;

  const blocks =
    data && typeof data === 'object' && 'agent_blocks' in data
      ? ((data as { agent_blocks?: Record<string, unknown> }).agent_blocks ?? {})
      : {};
  const assessment = (blocks as Record<string, unknown>).assessment as
    | Record<string, unknown>
    | undefined;
  const translation = (blocks as Record<string, unknown>).translation as
    | Record<string, unknown>
    | undefined;
  const validation = (blocks as Record<string, unknown>).validation as
    | Record<string, unknown>
    | undefined;

  const scores = assessment?.scores as Record<string, number> | undefined;
  const turtle = (translation?.turtle as string | undefined) ?? '';
  const tripleCount = translation?.triple_count;
  const stagingReached = validation?.staging_checkpoint_reached;

  return (
    <div
      className={cn(
        'fixed inset-y-0 right-0 z-40 flex w-[560px] flex-col border-l border-slate-200 bg-white shadow-xl transition-transform',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
      role="dialog"
      aria-label={t('supervision.drawer.title')}
    >
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
        <div className="min-w-0">
          <h2 className="text-h2 text-brand-900">{t('supervision.drawer.title')}</h2>
          <p className="truncate font-mono text-small text-slate-500" title={run.source_url}>
            {run.source_url || run.source_id}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {run.final_status && (
              <Badge variant={run.final_status.includes('halt') ? 'error' : 'success'}>
                {run.final_status}
              </Badge>
            )}
            {run.error_types.map((et) => (
              <Badge key={et} variant="error">
                {et}
              </Badge>
            ))}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('common:actions.close')}>
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => {
            setRetryResult(null);
            retryMut.mutate(run.source_id);
          }}
          disabled={retryMut.isPending}
        >
          {retryMut.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RotateCw className="mr-1 h-3.5 w-3.5" aria-hidden />
          )}
          {t('supervision.drawer.retry')}
        </Button>
        {retryResult && (
          <span className="flex items-center gap-1 text-small text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            {t('supervision.drawer.retryQueued', { runId: retryResult.runId.slice(0, 8) })}
          </span>
        )}
        {retryMut.isError && (
          <span className="text-small text-red-700">{apiError(retryMut.error)}</span>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 text-small">
        {isLoading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('common:status.loading')}
          </div>
        )}
        {isError && (
          <p className="text-amber-700">{t('supervision.drawer.notFound')}</p>
        )}
        {data && (
          <div className="space-y-4">
            <Section title={t('supervision.related.title')}>
              <RelatedErrors sourceId={run.source_id} runId={run.run_id} />
            </Section>

            {scores && (
              <Section title={t('supervision.drawer.assessmentScores')}>
                <ul className="grid grid-cols-2 gap-2">
                  {Object.entries(scores).map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-2 rounded bg-slate-50 px-2 py-1">
                      <span className="text-slate-600">{k}</span>
                      <span className="font-mono">
                        {typeof v === 'number' ? v.toFixed(2) : String(v)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {translation && (
              <Section
                title={t('supervision.drawer.translation', {
                  triples: typeof tripleCount === 'number' ? tripleCount : '—',
                })}
              >
                {turtle ? (
                  <pre className="max-h-72 overflow-auto rounded bg-slate-900 px-3 py-2 font-mono text-small text-slate-100">
                    {turtle}
                  </pre>
                ) : (
                  <p className="text-slate-500">{t('supervision.drawer.noTurtle')}</p>
                )}
              </Section>
            )}

            {validation && (
              <Section title={t('supervision.drawer.validation')}>
                <ul className="space-y-1">
                  <li>
                    <span className="text-slate-600">staging_checkpoint_reached: </span>
                    <span className="font-mono">{String(stagingReached)}</span>
                  </li>
                  <li>
                    <span className="text-slate-600">run_status: </span>
                    <span className="font-mono">{String(validation.run_status)}</span>
                  </li>
                </ul>
              </Section>
            )}

            <Section title={t('supervision.drawer.rawDrc')}>
              <pre className="max-h-72 overflow-auto rounded bg-slate-50 px-3 py-2 font-mono text-small text-slate-700">
                {JSON.stringify(data, null, 2)}
              </pre>
            </Section>

            {run.error_types.length > 0 && (
              <Link
                to={`/studio/errors?source_id=${run.source_id}`}
                className="inline-flex items-center gap-1 text-accent-700 hover:text-accent"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {t('supervision.drawer.openErrors')}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-1 text-h4 text-slate-700">{title}</h3>
      {children}
    </section>
  );
}
